import { notification } from 'antd';
import _ from 'lodash';
import { isMobileOnly } from 'react-device-detect';
import { history } from 'umi';

import { bindServices } from '@/services/api';

const api = bindServices('customListConfig');
import { CoreEnums } from '@/utils/domain/constants';
import { isJsonLike, isNullOrEmpty, sortFn, toPaginate } from '@/utils/infrastructure';
import { queryExport, runListCommand } from '../service';
import { commandByKey, fieldAndColumnMapper, localCustomListCommands } from './utils';

const {
  CustomListType,
  CustomListCommandType,
  CustomListDataOrigin,
  FormFieldStringType,
  FormFieldType,
  CustomListFieldType,
} = CoreEnums;

const handleFieldDisplayText = (item, data = []) => {
  const { field, internalName } = _.head(data) || {};

  if (!internalName) return;
  let val = item[internalName];

  switch (field?.type) {
    case FormFieldStringType.SelectorArea:
    case FormFieldStringType.SelectorPosition:
    case FormFieldStringType.SelectorUser:
    case FormFieldStringType.SelectorUserGroup:
    case FormFieldStringType.DataBaseRemoteList:
    case FormFieldStringType.DataBaseRemoteSearch:
    case FormFieldStringType.WebServiceRemoteList:
    case FormFieldStringType.WebServiceRemoteSearch:
    case FormFieldStringType.FieldDynamicEntityList:
    case FormFieldStringType.FieldDynamicEntitySearch:
    case FormFieldStringType.Choice:
    case FormFieldType.SelectorArea:
    case FormFieldType.SelectorPosition:
    case FormFieldType.SelectorUser:
    case FormFieldType.SelectorUserGroup:
    case FormFieldType.DataBaseRemoteList:
    case FormFieldType.DataBaseRemoteSearch:
    case FormFieldType.WebServiceRemoteList:
    case FormFieldType.WebServiceRemoteSearch:
    case FormFieldType.FieldDynamicEntityList:
    case FormFieldType.FieldDynamicEntitySearch:
    case FormFieldType.Choice: {
      if (isJsonLike(val)) {
        val = JSON.parse(val);
        const { text, displayText } = val?.items?.[0] || val || {};
        return displayText || text;
      }

      return val;
    }

    default:
      return val;
  }
};

const transformCmdKeys = (type, items = [], fields = []) => {
  try {
    return _.map(items, (item) => {
      const isApproveNoListType =
        item?.[`Command_${commandByKey[CustomListCommandType.ApproveRequest]}`] &&
        type !== CustomListType.CardsList &&
        type !== CustomListType.List;

      const temp = fields.filter((o) => {
        return ![
          CustomListFieldType.InstanceName,
          CustomListFieldType.InstanceStatus,
          CustomListFieldType.InstancePercentCompleted,
        ].includes(o.type);
      });

      if (type === CustomListType.CardsList && fields?.length > 0) {
        const percentage = _.find(fields, { type: CustomListFieldType.InstancePercentCompleted });
        const statusField = _.find(fields, { type: CustomListFieldType.InstanceStatus });

        const transformed = temp
          .slice(1)
          .map((o) => {
            if (_.isNil(item?.[o.internalName])) return null;

            const value = isJsonLike(item?.[o.internalName])
              ? JSON.parse(item?.[o.internalName])?.displayText
              : item?.[o.internalName];

            return { name: o.displayText, value };
          })
          .filter(Boolean);

        Object.assign(item, {
          fieldInstanceStatus: statusField?.internalName,
          fieldDisplayText: handleFieldDisplayText(item, temp),
          instancePercentCompleted: percentage?.internalName,
          fields: transformed,
        });
      }

      return {
        ...item,
        isCustomListCard: type === CustomListType.CardsList,
        ...(isApproveNoListType && {
          [`Command_${commandByKey[CustomListCommandType.RejectRequest]}`]: 1,
        }),
      };
    });
  } catch (err) {
    console.error('ERR_CUSTOM_LIST_TRANSF', err?.message || err);
  }
};

const meta = {
  page: 1,
  limit: 5,
  count: 0,
  totalPages: 0,
};

const notify = {
  edited: false,
  created: false,
  removed: false,
};

const initialState = {
  data: [],
  customListConfig: null,
  filtersConfig: [],
  pagination: meta,
  searchCriteria: null,
  ...notify,
  addFormVisible: false,
  dynamicEntityId: null,
  listId: null,
  currentItem: {},
  orderBy: '',
  bulkCommand: null,
  readPriority: false,
};

const Model = {
  namespace: 'customListConfig',
  state: initialState,
  effects: {
    *fetchConfig({ payload }, { call, put }) {
      try {
        const config = yield call(api.getById, {
          route: 'list/getConfig',
          id: payload.id,
          params: payload.params || {},
        });
        yield put({
          type: 'queryConfig',
          payload: { ...payload, config },
        });
      } catch (e) {
        const route = payload.embedConfig ? payload.embedConfig.redirectRoute : '/custom-lists';
        history.replace(route);
      }
    },

    *fetchData({ payload }, { call, put, select }) {
      try {
        const { readPriority, byFields } = yield select((state) => {
          return {
            readPriority: state.customListConfig?.readPriority,
            byFields: state.customListConfig?.groupByFields?.length > 0,
          };
        });

        const res = yield call(api.getById, {
          route: 'list/getData',
          method: 'post',
          readPriority,
          ...payload,
          ...(byFields && { limit: 100_000 }),
        });

        yield put({
          type: byFields ? 'queryByFieldData' : 'queryData',
          payload: {
            ...res,
            ..._.pick(payload, ['limit', 'page']),
          },
        });
      } catch (e) {}
    },

    *fetchById({ payload: id }, { call, put }) {
      try {
        const res = yield call(api.getById, {
          route: 'customList',
          id,
          params: { select: 'id,name' },
        });
        yield put({ type: 'queryById', payload: res });
      } catch (e) {}
    },

    *editBy({ payload }, { call, put }) {
      try {
        yield call(api.update, {
          route: `list/${payload.listId}/${payload.itemId}`,
          method: 'patch',
          data: payload.data || {},
        });
        yield put({ type: 'queryEdit', payload: true });
      } catch (e) {}
    },

    *create({ payload }, { call, put }) {
      try {
        yield call(api.create, {
          route: `list/${payload.listId}`,
          data: payload.data,
        });
        yield put({ type: 'queryCreate', payload: true });
      } catch (e) {}
    },

    *remove({ payload }, { call, put }) {
      try {
        yield call(api.apiRoute, {
          route: `list/${payload.listId}/${payload.itemId}`,
          method: 'delete',
        });
        yield put({ type: 'queryRemove', payload: true });
      } catch (e) {}
    },

    *runCommand({ payload }, { call, put }) {
      try {
        yield put({
          type: 'global/isCmdRunning',
          payload: _.pick(payload, ['command', 'rowIndex']),
        });
        yield call(runListCommand, payload);
        yield put({ type: 'queryRunCommand', payload });
      } catch (e) {
      } finally {
        yield put({ type: 'global/isCmdRunning' });
      }
    },

    *bulkCommand({ payload, callback }, { call, put }) {
      try {
        yield call(api.bulkCommand, payload, callback);
        yield put({ type: 'global/ongoingOps' });
        yield put({ type: 'queryBulkCommand', payload });
      } catch (e) {
        yield put({ type: 'global/ongoingOps' });
        yield put({
          type: 'queryBulkCommand',
          payload: { command: -1 },
        });
      }
    },

    *export({ payload }, { call }) {
      try {
        const res = yield call(queryExport, payload);
        if (res?.message) {
          const [message, description] = _.split(res.message, '.');
          notification.destroy();
          notification.success({
            ...(message && { message }),
            ...(description && { description }),
            ...(!isMobileOnly && { className: 'ace-export-notice' }),
            style: { minWidth: 600 },
            duration: 10,
          });
        } else {
          const pre = `data:${res.contentType};base64,${res.content}`;
          const a = document.createElement('a');
          a.setAttribute('download', res.fileName);
          a.setAttribute('href', pre);
          a.style.display = 'none';
          document.body.appendChild(a);
          a.click();
          a?.remove();
        }
      } catch (e) {}
    },

    *activeGroupByField({ payload }, { put }) {
      yield put({ type: 'queryGroupByField', payload });
    },

    *search({ payload }, { put }) {
      yield put({ type: 'querySearch', payload });
    },

    *paginate({ payload }, { put }) {
      yield put({ type: 'queryPage', payload });
    },

    *setOrderBy({ payload }, { put }) {
      yield put({ type: 'orderBy', payload });
    },

    *toggleAddForm({ payload }, { put }) {
      yield put({ type: 'queryFormVisibility', payload });
    },

    *cleanup(__, { put }) {
      yield put({ type: 'queryCleanup' });
    },
  },
  reducers: {
    queryConfig(state, { payload }) {
      const {
        fields = [],
        commands = [],
        requestCommands = [],
        type,
        name,
        dataOrigin,
        mapListLegends,
        ...config
      } = payload.config;

      const groupedByFields = config.groupByFields?.length > 0;
      const listId = isNullOrEmpty(payload?.embedConfig) ? null : payload.id;
      const { formFields, filtersConfig, columns } = fieldAndColumnMapper(fields, listId);

      return {
        ...state,
        ...notify,
        listId: payload.id,
        executedCommand: null,
        filtersConfig,
        type,
        ...config,
        data: [],
        customListConfig: {
          name,
          type,
          columns,
          mapListLegends,
          fields: fields.sort(sortFn),
          formFields,
          dataOrigin,
          groupedByFields,
          ...(requestCommands.length > 0 && { requestCommands }),
          commands: [
            ...commands,
            ...(type === CustomListType.List || type === CustomListType.CardsList
              ? []
              : localCustomListCommands),
          ].filter(Boolean),
          ...(dataOrigin === CustomListDataOrigin.DynamicEntity &&
            type !== CustomListType.CardsList && {
              shouldEdit: _.some(commands, { command: CustomListCommandType.EntityEdit }),
              shouldDelete: _.some(commands, { command: CustomListCommandType.EntityDelete }),
            }),
        },
      };
    },

    queryData(state, { payload }) {
      const {
        type,
        customListConfig: { fields = [] },
      } = state;

      return {
        ...state,
        ...notify,
        readPriority: false,
        data: transformCmdKeys(type, payload.items, fields),
        executedCommand: null,
        pagination: {
          ...state.pagination,
          ...toPaginate(payload),
        },
      };
    },

    queryByFieldData(state, { payload }) {
      const {
        type,
        groupByFields = [],
        groupByPercents = [],
        customListConfig: { fields = [] },
      } = state;

      const data = [];
      const keys = [];
      const result = {};

      for (const item of groupByFields) {
        const field = _.find(fields, { id: item.customListFieldId });
        if (field) {
          const grouped = _.groupBy(payload.items, field.internalName);
          const transformed = Object.entries(grouped).reduce((lhs, [key, items]) => {
            const ordered = _.orderBy(
              items,
              ['ProcessInstance_Status', 'created'],
              ['asc', 'desc'],
            );

            return {
              ...lhs,
              [key]: {
                items: transformCmdKeys(type, ordered, fields),
                groupByField: field.internalName,
                meta: toPaginate({ ...meta, totalCount: items.length }),
              },
            };
          }, {});

          Object.assign(result, transformed);
          keys.push(...Object.keys(transformed));
        }
      }

      for (const key of keys) {
        const { items = [], groupByField } = result[key] || [];

        const filtered = _.filter(items, (item) => {
          return _.some(groupByPercents, (group) => {
            const field = _.find(fields, { id: group.customListFieldId });
            return field ? item[field.internalName] == group.filterValue : false;
          });
        });

        data.push({
          [groupByField]: key,
          count: items.length || 0,
          percent: Math.round(Math.max(filtered.length / items.length, 0) * 100),
          groupByField,
        });
      }

      return {
        ...state,
        ...notify,
        data,
        executedCommand: null,
        readPriority: false,
        allGroupedFieldsData: result,
        pagination: {
          ...state.pagination,
          ...toPaginate({ ...payload, totalCount: data.length }),
        },
        activeGroupByField: localStorage.getItem('x-temp-activeGroupByField')
          ? JSON.parse(localStorage.getItem('x-temp-activeGroupByField'))
          : null,
      };
    },

    queryGroupByField(state, { payload }) {
      return {
        ...state,
        activeGroupByField: payload,
      };
    },

    queryById(state, { payload }) {
      return {
        ...state,
        currentItem: payload,
      };
    },

    queryEdit(state, { payload }) {
      return {
        ...state,
        edited: payload,
      };
    },

    queryCreate(state, { payload }) {
      return {
        ...state,
        created: payload,
        addFormVisible: false,
      };
    },

    queryRemove(state, { payload }) {
      return {
        ...state,
        removed: payload,
      };
    },

    queryRunCommand(state, { payload }) {
      return {
        ...state,
        executedCommand: payload.command,
        readPriority: true,
      };
    },

    queryBulkCommand(state, { payload }) {
      return {
        ...state,
        executedCommand: payload.command,
        readPriority: true,
      };
    },

    queryFormVisibility(state, { payload }) {
      return {
        ...state,
        addFormVisible: payload ?? false,
      };
    },

    querySearch(state, { payload }) {
      return {
        ...state,
        searchCriteria: payload,
      };
    },

    orderBy(state, { payload }) {
      return {
        ...state,
        orderBy: payload,
      };
    },

    queryCleanup() {
      return initialState;
    },
  },
};

export default Model;
